// <auto-generated>
// This file is automatically generated from DSL.
// Do not modify this file directly. Changes will be overwritten.
// Source: dsl/entities/IntegrationJob.yaml
// Generated: 2026-03-17T14:51:31.212Z
// </auto-generated>

import { JsonSchema, UISchemaElement, RuleEffect } from '@jsonforms/core';

/** Extended JsonSchema that allows custom properties like enumNames, x-lookup, enumSource. */
type ExtendedJsonSchema = JsonSchema & Record<string, unknown>;

/**
 * JSON Schema for IntegrationJob (CREATE)
 */
export const IntegrationJobCreateSchema = {
  "type": "object",
  "title": "IntegrationJob",
  "description": "Runtime configuration for an integration — connects a mapping source to a connection",
  "properties": {
    "name": {
      "title": "Name",
      "description": "Human-readable name for this integration job (Must be unique)",
      "type": "string",
      "maxLength": 200
    },
    "integrationConnectionId": {
      "title": "Connection",
      "description": "Connection to use for this job",
      "type": "string",
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "IntegrationConnection",
        "module": "integration",
        "displayField": "name",
        "searchFields": [
          "name"
        ],
        "additionalFields": [
          "provider"
        ],
        "minSearchLength": 1,
        "pageSize": 10,
        "recentlyUsedSlot": "integration.IntegrationJob.integrationConnectionId"
      }
    },
    "type": {
      "title": "Type",
      "description": "Import pulls data in, Export sends it out. The two halves of the job share almost nothing, so this is what most of the form keys off.",
      "type": "string",
      "enum": [
        "IMPORT",
        "EXPORT",
        "CALL"
      ],
      "enumNames": [
        "Import",
        "Export",
        "External call"
      ],
      "enumOrdinals": [
        0,
        1,
        3
      ],
      "enumType": "IntegrationJobType"
    },
    "inboundTransport": {
      "title": "Transport",
      "description": "Where this job's data comes from: it fetches files from the connection, or partners post them to the job's own endpoint.",
      "type": "string",
      "enum": [
        "FETCH",
        "PUSH"
      ],
      "enumNames": [
        "Fetch from connection",
        "Push channel"
      ],
      "enumOrdinals": [
        0,
        1
      ],
      "enumType": "IntegrationInboundTransport",
      "default": "FETCH"
    },
    "commandMode": {
      "title": "Command mode",
      "description": "How this job interacts with the command pipeline",
      "type": "string",
      "enum": [
        "STANDARD",
        "BATCH",
        "MIGRATION"
      ],
      "enumNames": [
        "Standard",
        "Batch",
        "Migration"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "IntegrationCommandMode"
    },
    "errorStrategy": {
      "title": "Error strategy",
      "description": "What a failed row does to the rest of the file: stop the run, log it and carry on, or set it aside as quarantined and carry on.",
      "type": "string",
      "enum": [
        "FAIL",
        "LOG_AND_CONTINUE",
        "QUARANTINE"
      ],
      "enumNames": [
        "Fail",
        "Log and Continue",
        "Quarantine"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "IntegrationErrorStrategy",
      "default": "QUARANTINE"
    },
    "maxRetries": {
      "title": "Max retries",
      "description": "Export: how many times a failed delivery is picked up again before the outbound record is left alone. External call: how many times a retryable transport failure (timeout, 5xx) is tried again before the call fails.",
      "type": "integer",
      "default": 3
    },
    "isActive": {
      "title": "Active",
      "description": "Enable/disable this integration job",
      "type": "boolean"
    },
    "mappingSourceType": {
      "title": "Mapping source",
      "description": "How the message is turned into records: a saved column mapping, a coded adapter, the system's own DTO shape, or a stylesheet on this job.",
      "type": "string",
      "enum": [
        "IMPORT_PROFILE",
        "CODED_ADAPTER",
        "DIRECT_DTO",
        "XSLT_TRANSFORM"
      ],
      "enumNames": [
        "Import Profile",
        "Coded Adapter",
        "Direct DTO",
        "XSLT Transform"
      ],
      "enumOrdinals": [
        0,
        1,
        2,
        3
      ],
      "enumType": "MappingSourceType"
    },
    "adapterName": {
      "title": "Adapter",
      "description": "Coded DSL adapter name",
      "type": [
        "string",
        "null"
      ],
      "format": "api-lookup",
      "x-lookup": {
        "apiMode": true,
        "endpoint": "/api/integration/integrationjob/coded-adapters",
        "valueField": "adapterName",
        "displayField": "displayName",
        "minSearchLength": 0
      }
    },
    "importProfileId": {
      "title": "Import profile",
      "description": "Import Profile for tabular imports",
      "type": [
        "string",
        "null"
      ],
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "ImportProfile",
        "module": "integration",
        "displayField": "name",
        "searchFields": [
          "name",
          "targetEntity"
        ],
        "additionalFields": [
          "targetEntity",
          "format"
        ],
        "minSearchLength": 0,
        "pageSize": 10,
        "recentlyUsedSlot": "integration.IntegrationJob.importProfileId"
      }
    },
    "targetEntityName": {
      "title": "Target entity",
      "description": "Target entity for Direct DTO and XSLT Transform integrations",
      "type": [
        "string",
        "null"
      ],
      "format": "api-lookup",
      "x-lookup": {
        "apiMode": true,
        "endpoint": "/api/integration/integrationjob/target-entities",
        "valueField": "entityName",
        "displayField": "displayName",
        "minSearchLength": 0
      }
    },
    "transformXslt": {
      "title": "Stylesheet (XSLT 1.0)",
      "description": "XSLT 1.0 stylesheet that turns the received XML into the canonical XML of the target entity (root = entity name, children = its field names, one repeated element per line collection). Compiled on save; edit and Reprocess a message to iterate.",
      "type": [
        "string",
        "null"
      ]
    },
    "inboundMessageType": {
      "title": "Endpoint name",
      "description": "The last part of the address partners post to: /ingest/v1/{endpoint name}. Lowercase letters and digits, words joined by single dashes. Must be unique within the tenant. (Must be unique)",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 100,
      "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$"
    },
    "messageIdPath": {
      "title": "Message id path",
      "description": "Where the message id assigned by the sender is read from: an XPath for XML (prefix ns is bound to the namespace of the root element), a JSON pointer for JSON. Empty: the X-Message-Id header, else the payload hash.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key1Path": {
      "title": "Key 1 path",
      "description": "Where Key 1 on the inbox row is read from, so a message can be found by what it is about: an XPath for XML (prefix ns is the root element's namespace; reach elements in another namespace with local-name()) or a JSON pointer for JSON. Read when the message is processed. Empty leaves the key empty.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key2Path": {
      "title": "Key 2 path",
      "description": "Where Key 2 on the inbox row is read from. Same kind of path as Key 1.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key3Path": {
      "title": "Key 3 path",
      "description": "Where Key 3 on the inbox row is read from. Same kind of path as Key 1.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "verificationProfile": {
      "title": "Verification profile",
      "description": "Standard-specific checks run before the transform: papinet (test flag, party identifiers, envelope vs document) or empty for none.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 50
    },
    "acceptTestFlag": {
      "title": "Accept test flag",
      "description": "The envelope TestFlag this channel accepts: on for a test channel, off for production. A mismatch fails the message.",
      "type": "boolean",
      "default": false
    },
    "inlineThresholdBytes": {
      "title": "Inline threshold (bytes)",
      "description": "Messages up to this size are stored on the inbox row; larger ones go to blob storage. Default 1 MB.",
      "type": [
        "integer",
        "null"
      ],
      "default": 1048576
    },
    "maxBodyBytes": {
      "title": "Max body (bytes)",
      "description": "Largest decompressed message accepted; above it the partner gets 413. Default 20 MB.",
      "type": [
        "integer",
        "null"
      ],
      "default": 20971520
    },
    "sourcePath": {
      "title": "Source path",
      "description": "Path relative to connection base path, e.g. 'products/inbound/'",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "deliveryPath": {
      "title": "Delivery path",
      "description": "Path relative to connection base path, e.g. 'budget/{yyyy}/{MM}'. Supports {yyyy}, {MM} and {dd}. Leave empty to deliver straight to the connection's base path.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500,
      "format": "path-preview",
      "x-path-preview": {
        "endpoint": "/api/integration/integrationjob/delivery-path-preview",
        "connectionProperty": "integrationConnectionId"
      }
    },
    "sourceFilePattern": {
      "title": "File pattern",
      "description": "File name glob, e.g. '*.csv', 'ORDER_*.xml'",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 200
    },
    "sourceEncoding": {
      "title": "File encoding",
      "description": "Character encoding of the files this job fetches, e.g. 'utf-8' or 'iso-8859-1'. Set it to what the sender writes; the wrong value shows up as garbled letters.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 40,
      "default": "utf-8"
    },
    "apiEndpoint": {
      "title": "Endpoint",
      "description": "Path appended to the connection's base URL, e.g. Distance/Calculate.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "callTimeoutSeconds": {
      "title": "Call timeout (s)",
      "description": "How long one attempt may take before it is abandoned and counted as a retryable failure.",
      "type": "integer",
      "default": 30
    },
    "retryBackoffSeconds": {
      "title": "Retry backoff (s)",
      "description": "How long to wait before a retryable failure is tried again (linear).",
      "type": "integer",
      "default": 10
    },
    "deadlineSeconds": {
      "title": "Deadline (s)",
      "description": "How long the whole request may stay open, queue wait included, before it is failed as timed out. Must exceed (call timeout + retry backoff) × (max retries + 1).",
      "type": "integer",
      "default": 600
    },
    "maxConcurrentCalls": {
      "title": "Max concurrent calls",
      "description": "Soft ceiling on calls in flight for this job at once; a call past it waits and is retried. Empty means the worker's own capacity is the only limit.",
      "type": [
        "integer",
        "null"
      ]
    },
    "processingMode": {
      "title": "Processing mode",
      "description": "Immediate or Deferred staging processing",
      "type": [
        "string",
        "null"
      ],
      "enum": [
        "IMMEDIATE",
        "DEFERRED",
        null
      ],
      "enumNames": [
        "Immediate",
        "Deferred",
        "None"
      ],
      "enumOrdinals": [
        0,
        1,
        null
      ],
      "enumType": "IntegrationProcessingMode"
    },
    "deliveryMode": {
      "title": "Delivery mode",
      "description": "Immediate or Batched outbound delivery",
      "type": [
        "string",
        "null"
      ],
      "enum": [
        "IMMEDIATE",
        "BATCHED",
        null
      ],
      "enumNames": [
        "Immediate",
        "Batched",
        "None"
      ],
      "enumOrdinals": [
        0,
        1,
        null
      ],
      "enumType": "IntegrationDeliveryMode"
    },
    "triggerEventType": {
      "title": "Trigger event",
      "description": "Event type that triggers this job automatically",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 200
    },
    "acknowledgementExpected": {
      "title": "Answers expected",
      "description": "Which answers the recipient sends for each document this job delivers. A job that expects answers delivers only documents its adapter names, each version once.",
      "type": "string",
      "enum": [
        "NONE",
        "RECEIPT",
        "RECEIPT_AND_ACCEPTANCE"
      ],
      "enumNames": [
        "None",
        "Receipt",
        "Receipt and acceptance"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "OutboundAcknowledgementExpectation",
      "default": "NONE"
    },
    "receiptDeadlineMinutes": {
      "title": "Receipt deadline (min)",
      "description": "Minutes after delivery by which the receipt must have arrived; past it the document is marked overdue. Empty: never overdue.",
      "type": [
        "integer",
        "null"
      ]
    },
    "acceptanceDeadlineMinutes": {
      "title": "Acceptance deadline (min)",
      "description": "Minutes after the receipt by which the acceptance or rejection must have arrived; past it the document is marked overdue. Empty: never overdue.",
      "type": [
        "integer",
        "null"
      ]
    },
    "defaultOrgUnitId": {
      "title": "Default org unit",
      "description": "Default org unit for records imported by this job",
      "type": [
        "string",
        "null"
      ],
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "OrgUnit",
        "module": "core",
        "displayField": "name",
        "searchFields": [
          "name",
          "code"
        ],
        "additionalFields": [],
        "minSearchLength": 0,
        "pageSize": 10,
        "presentation": "tree"
      }
    },
    "sourceFileConfig": {
      "title": "File config",
      "description": "Source file lifecycle settings at the provider",
      "type": [
        "object",
        "null"
      ],
      "properties": {
        "deleteAfterProcess": {
          "title": "Delete source file",
          "description": "Delete the source file from the provider after successful processing",
          "type": [
            "boolean",
            "null"
          ]
        },
        "archiveEnabled": {
          "title": "Archive source file",
          "description": "Move the source file to an archive path after processing",
          "type": [
            "boolean",
            "null"
          ]
        },
        "archivePath": {
          "title": "Archive path",
          "description": "Archive path pattern, e.g. 'archive/products/{yyyy}/{MM}/{dd}'",
          "type": [
            "string",
            "null"
          ],
          "maxLength": 500
        }
      }
    }
  },
  "required": [
    "name",
    "integrationConnectionId",
    "type",
    "inboundTransport",
    "commandMode",
    "errorStrategy",
    "maxRetries",
    "isActive",
    "mappingSourceType",
    "acceptTestFlag",
    "callTimeoutSeconds",
    "retryBackoffSeconds",
    "deadlineSeconds",
    "acknowledgementExpected"
  ]
} as unknown as ExtendedJsonSchema;

/**
 * UI Schema for IntegrationJob (CREATE)
 */
export const IntegrationJobCreateUiSchema: UISchemaElement = {
  "type": "GridLayout",
  "options": {
    "columns": 12,
    "density": "compact",
    "variant": "standard"
  },
  "elements": [
    {
      "type": "Group",
      "label": "Job",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/name",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/isActive",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/type",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/inboundTransport",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/integrationConnectionId",
          "options": {
            "gridColumn": "span 6",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "IntegrationConnection",
              "module": "integration",
              "displayField": "name",
              "searchFields": [
                "name"
              ],
              "additionalFields": [
                "provider"
              ],
              "minSearchLength": 1
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/commandMode",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/defaultOrgUnitId",
          "options": {
            "gridColumn": "span 6",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "OrgUnit",
              "module": "core",
              "displayField": "name",
              "searchFields": [
                "name",
                "code"
              ],
              "additionalFields": [],
              "minSearchLength": 0,
              "presentation": "tree"
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Job",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Mapping Source",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/mappingSourceType",
          "options": {
            "gridColumn": "span 12",
            "allowedValuesWhen": [
              {
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                "values": [
                  "CODED_ADAPTER"
                ]
              },
              {
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                },
                "values": [
                  "CODED_ADAPTER"
                ]
              }
            ]
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/importProfileId",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "ImportProfile",
              "module": "integration",
              "displayField": "name",
              "searchFields": [
                "name",
                "targetEntity"
              ],
              "additionalFields": [
                "targetEntity",
                "format"
              ],
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "IMPORT_PROFILE"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/adapterName",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "apiMode": true,
              "endpoint": "/api/integration/integrationjob/coded-adapters",
              "valueField": "adapterName",
              "displayField": "displayName",
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "CODED_ADAPTER"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/targetEntityName",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "apiMode": true,
              "endpoint": "/api/integration/integrationjob/target-entities",
              "valueField": "entityName",
              "displayField": "displayName",
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "enum": [
                  "DIRECT_DTO",
                  "XSLT_TRANSFORM"
                ]
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.MappingSource",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Error Handling",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/errorStrategy",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxRetries",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "enum": [
                  "EXPORT",
                  "CALL"
                ]
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.ErrorHandling",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Source Files",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/deleteAfterProcess",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/archivePath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                },
                {
                  "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
                  "schema": {
                    "const": true
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.SourceFiles",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "File Source",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/sourcePath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFilePattern",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceEncoding",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                },
                {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "enum": [
                      "IMPORT_PROFILE",
                      "DIRECT_DTO"
                    ]
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.FileSource",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Processing",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/processingMode",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deliveryMode",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deliveryPath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Processing",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Channel",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/inboundMessageType",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/messageIdPath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/verificationProfile",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                },
                {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "XSLT_TRANSFORM"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Channel",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Options",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/acceptTestFlag",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/inlineThresholdBytes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxBodyBytes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Options",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Inbox search keys",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/key1Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/key2Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/key3Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Inboxsearchkeys",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Transform",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/transformXslt",
          "options": {
            "gridColumn": "span 12",
            "contentFormat": "xml",
            "multi": true,
            "rows": 5
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "XSLT_TRANSFORM"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Transform",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Trigger",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/triggerEventType",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Trigger",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Answers",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/acknowledgementExpected",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/receiptDeadlineMinutes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                {
                  "scope": "#/properties/acknowledgementExpected",
                  "schema": {
                    "enum": [
                      "RECEIPT",
                      "RECEIPT_AND_ACCEPTANCE"
                    ]
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/acceptanceDeadlineMinutes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                {
                  "scope": "#/properties/acknowledgementExpected",
                  "schema": {
                    "const": "RECEIPT_AND_ACCEPTANCE"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Answers",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Call",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/apiEndpoint",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/callTimeoutSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/retryBackoffSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deadlineSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxConcurrentCalls",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Call",
      "options": {
        "gridColumn": "span 4"
      }
    }
  ]
};

/**
 * JSON Schema for IntegrationJob (UPDATE)
 */
export const IntegrationJobUpdateSchema = {
  "type": "object",
  "title": "IntegrationJob",
  "description": "Runtime configuration for an integration — connects a mapping source to a connection",
  "properties": {
    "name": {
      "title": "Name",
      "description": "Human-readable name for this integration job (Must be unique)",
      "type": "string",
      "maxLength": 200
    },
    "integrationConnectionId": {
      "title": "Connection",
      "description": "Connection to use for this job",
      "type": "string",
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "IntegrationConnection",
        "module": "integration",
        "displayField": "name",
        "searchFields": [
          "name"
        ],
        "additionalFields": [
          "provider"
        ],
        "minSearchLength": 1,
        "pageSize": 10,
        "recentlyUsedSlot": "integration.IntegrationJob.integrationConnectionId"
      }
    },
    "type": {
      "title": "Type",
      "description": "Import pulls data in, Export sends it out. The two halves of the job share almost nothing, so this is what most of the form keys off.",
      "type": "string",
      "enum": [
        "IMPORT",
        "EXPORT",
        "CALL"
      ],
      "enumNames": [
        "Import",
        "Export",
        "External call"
      ],
      "enumOrdinals": [
        0,
        1,
        3
      ],
      "enumType": "IntegrationJobType"
    },
    "inboundTransport": {
      "title": "Transport",
      "description": "Where this job's data comes from: it fetches files from the connection, or partners post them to the job's own endpoint.",
      "type": "string",
      "enum": [
        "FETCH",
        "PUSH"
      ],
      "enumNames": [
        "Fetch from connection",
        "Push channel"
      ],
      "enumOrdinals": [
        0,
        1
      ],
      "enumType": "IntegrationInboundTransport",
      "default": "FETCH"
    },
    "commandMode": {
      "title": "Command mode",
      "description": "How this job interacts with the command pipeline",
      "type": "string",
      "enum": [
        "STANDARD",
        "BATCH",
        "MIGRATION"
      ],
      "enumNames": [
        "Standard",
        "Batch",
        "Migration"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "IntegrationCommandMode"
    },
    "errorStrategy": {
      "title": "Error strategy",
      "description": "What a failed row does to the rest of the file: stop the run, log it and carry on, or set it aside as quarantined and carry on.",
      "type": "string",
      "enum": [
        "FAIL",
        "LOG_AND_CONTINUE",
        "QUARANTINE"
      ],
      "enumNames": [
        "Fail",
        "Log and Continue",
        "Quarantine"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "IntegrationErrorStrategy",
      "default": "QUARANTINE"
    },
    "maxRetries": {
      "title": "Max retries",
      "description": "Export: how many times a failed delivery is picked up again before the outbound record is left alone. External call: how many times a retryable transport failure (timeout, 5xx) is tried again before the call fails.",
      "type": "integer",
      "default": 3
    },
    "isActive": {
      "title": "Active",
      "description": "Enable/disable this integration job",
      "type": "boolean"
    },
    "mappingSourceType": {
      "title": "Mapping source",
      "description": "How the message is turned into records: a saved column mapping, a coded adapter, the system's own DTO shape, or a stylesheet on this job.",
      "type": "string",
      "enum": [
        "IMPORT_PROFILE",
        "CODED_ADAPTER",
        "DIRECT_DTO",
        "XSLT_TRANSFORM"
      ],
      "enumNames": [
        "Import Profile",
        "Coded Adapter",
        "Direct DTO",
        "XSLT Transform"
      ],
      "enumOrdinals": [
        0,
        1,
        2,
        3
      ],
      "enumType": "MappingSourceType"
    },
    "adapterName": {
      "title": "Adapter",
      "description": "Coded DSL adapter name",
      "type": [
        "string",
        "null"
      ],
      "format": "api-lookup",
      "x-lookup": {
        "apiMode": true,
        "endpoint": "/api/integration/integrationjob/coded-adapters",
        "valueField": "adapterName",
        "displayField": "displayName",
        "minSearchLength": 0
      }
    },
    "importProfileId": {
      "title": "Import profile",
      "description": "Import Profile for tabular imports",
      "type": [
        "string",
        "null"
      ],
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "ImportProfile",
        "module": "integration",
        "displayField": "name",
        "searchFields": [
          "name",
          "targetEntity"
        ],
        "additionalFields": [
          "targetEntity",
          "format"
        ],
        "minSearchLength": 0,
        "pageSize": 10,
        "recentlyUsedSlot": "integration.IntegrationJob.importProfileId"
      }
    },
    "targetEntityName": {
      "title": "Target entity",
      "description": "Target entity for Direct DTO and XSLT Transform integrations",
      "type": [
        "string",
        "null"
      ],
      "format": "api-lookup",
      "x-lookup": {
        "apiMode": true,
        "endpoint": "/api/integration/integrationjob/target-entities",
        "valueField": "entityName",
        "displayField": "displayName",
        "minSearchLength": 0
      }
    },
    "transformXslt": {
      "title": "Stylesheet (XSLT 1.0)",
      "description": "XSLT 1.0 stylesheet that turns the received XML into the canonical XML of the target entity (root = entity name, children = its field names, one repeated element per line collection). Compiled on save; edit and Reprocess a message to iterate.",
      "type": [
        "string",
        "null"
      ]
    },
    "inboundMessageType": {
      "title": "Endpoint name",
      "description": "The last part of the address partners post to: /ingest/v1/{endpoint name}. Lowercase letters and digits, words joined by single dashes. Must be unique within the tenant. (Must be unique)",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 100,
      "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$"
    },
    "messageIdPath": {
      "title": "Message id path",
      "description": "Where the message id assigned by the sender is read from: an XPath for XML (prefix ns is bound to the namespace of the root element), a JSON pointer for JSON. Empty: the X-Message-Id header, else the payload hash.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key1Path": {
      "title": "Key 1 path",
      "description": "Where Key 1 on the inbox row is read from, so a message can be found by what it is about: an XPath for XML (prefix ns is the root element's namespace; reach elements in another namespace with local-name()) or a JSON pointer for JSON. Read when the message is processed. Empty leaves the key empty.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key2Path": {
      "title": "Key 2 path",
      "description": "Where Key 2 on the inbox row is read from. Same kind of path as Key 1.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "key3Path": {
      "title": "Key 3 path",
      "description": "Where Key 3 on the inbox row is read from. Same kind of path as Key 1.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "verificationProfile": {
      "title": "Verification profile",
      "description": "Standard-specific checks run before the transform: papinet (test flag, party identifiers, envelope vs document) or empty for none.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 50
    },
    "acceptTestFlag": {
      "title": "Accept test flag",
      "description": "The envelope TestFlag this channel accepts: on for a test channel, off for production. A mismatch fails the message.",
      "type": "boolean",
      "default": false
    },
    "inlineThresholdBytes": {
      "title": "Inline threshold (bytes)",
      "description": "Messages up to this size are stored on the inbox row; larger ones go to blob storage. Default 1 MB.",
      "type": [
        "integer",
        "null"
      ],
      "default": 1048576
    },
    "maxBodyBytes": {
      "title": "Max body (bytes)",
      "description": "Largest decompressed message accepted; above it the partner gets 413. Default 20 MB.",
      "type": [
        "integer",
        "null"
      ],
      "default": 20971520
    },
    "sourcePath": {
      "title": "Source path",
      "description": "Path relative to connection base path, e.g. 'products/inbound/'",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "deliveryPath": {
      "title": "Delivery path",
      "description": "Path relative to connection base path, e.g. 'budget/{yyyy}/{MM}'. Supports {yyyy}, {MM} and {dd}. Leave empty to deliver straight to the connection's base path.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500,
      "format": "path-preview",
      "x-path-preview": {
        "endpoint": "/api/integration/integrationjob/delivery-path-preview",
        "connectionProperty": "integrationConnectionId"
      }
    },
    "sourceFilePattern": {
      "title": "File pattern",
      "description": "File name glob, e.g. '*.csv', 'ORDER_*.xml'",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 200
    },
    "sourceEncoding": {
      "title": "File encoding",
      "description": "Character encoding of the files this job fetches, e.g. 'utf-8' or 'iso-8859-1'. Set it to what the sender writes; the wrong value shows up as garbled letters.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 40,
      "default": "utf-8"
    },
    "apiEndpoint": {
      "title": "Endpoint",
      "description": "Path appended to the connection's base URL, e.g. Distance/Calculate.",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 500
    },
    "callTimeoutSeconds": {
      "title": "Call timeout (s)",
      "description": "How long one attempt may take before it is abandoned and counted as a retryable failure.",
      "type": "integer",
      "default": 30
    },
    "retryBackoffSeconds": {
      "title": "Retry backoff (s)",
      "description": "How long to wait before a retryable failure is tried again (linear).",
      "type": "integer",
      "default": 10
    },
    "deadlineSeconds": {
      "title": "Deadline (s)",
      "description": "How long the whole request may stay open, queue wait included, before it is failed as timed out. Must exceed (call timeout + retry backoff) × (max retries + 1).",
      "type": "integer",
      "default": 600
    },
    "maxConcurrentCalls": {
      "title": "Max concurrent calls",
      "description": "Soft ceiling on calls in flight for this job at once; a call past it waits and is retried. Empty means the worker's own capacity is the only limit.",
      "type": [
        "integer",
        "null"
      ]
    },
    "processingMode": {
      "title": "Processing mode",
      "description": "Immediate or Deferred staging processing",
      "type": [
        "string",
        "null"
      ],
      "enum": [
        "IMMEDIATE",
        "DEFERRED",
        null
      ],
      "enumNames": [
        "Immediate",
        "Deferred",
        "None"
      ],
      "enumOrdinals": [
        0,
        1,
        null
      ],
      "enumType": "IntegrationProcessingMode"
    },
    "deliveryMode": {
      "title": "Delivery mode",
      "description": "Immediate or Batched outbound delivery",
      "type": [
        "string",
        "null"
      ],
      "enum": [
        "IMMEDIATE",
        "BATCHED",
        null
      ],
      "enumNames": [
        "Immediate",
        "Batched",
        "None"
      ],
      "enumOrdinals": [
        0,
        1,
        null
      ],
      "enumType": "IntegrationDeliveryMode"
    },
    "triggerEventType": {
      "title": "Trigger event",
      "description": "Event type that triggers this job automatically",
      "type": [
        "string",
        "null"
      ],
      "maxLength": 200
    },
    "acknowledgementExpected": {
      "title": "Answers expected",
      "description": "Which answers the recipient sends for each document this job delivers. A job that expects answers delivers only documents its adapter names, each version once.",
      "type": "string",
      "enum": [
        "NONE",
        "RECEIPT",
        "RECEIPT_AND_ACCEPTANCE"
      ],
      "enumNames": [
        "None",
        "Receipt",
        "Receipt and acceptance"
      ],
      "enumOrdinals": [
        0,
        1,
        2
      ],
      "enumType": "OutboundAcknowledgementExpectation",
      "default": "NONE"
    },
    "receiptDeadlineMinutes": {
      "title": "Receipt deadline (min)",
      "description": "Minutes after delivery by which the receipt must have arrived; past it the document is marked overdue. Empty: never overdue.",
      "type": [
        "integer",
        "null"
      ]
    },
    "acceptanceDeadlineMinutes": {
      "title": "Acceptance deadline (min)",
      "description": "Minutes after the receipt by which the acceptance or rejection must have arrived; past it the document is marked overdue. Empty: never overdue.",
      "type": [
        "integer",
        "null"
      ]
    },
    "defaultOrgUnitId": {
      "title": "Default org unit",
      "description": "Default org unit for records imported by this job",
      "type": [
        "string",
        "null"
      ],
      "format": "entity-lookup",
      "x-lookup": {
        "entity": "OrgUnit",
        "module": "core",
        "displayField": "name",
        "searchFields": [
          "name",
          "code"
        ],
        "additionalFields": [],
        "minSearchLength": 0,
        "pageSize": 10,
        "presentation": "tree"
      }
    },
    "sourceFileConfig": {
      "title": "File config",
      "description": "Source file lifecycle settings at the provider",
      "type": [
        "object",
        "null"
      ],
      "properties": {
        "deleteAfterProcess": {
          "title": "Delete source file",
          "description": "Delete the source file from the provider after successful processing",
          "type": [
            "boolean",
            "null"
          ]
        },
        "archiveEnabled": {
          "title": "Archive source file",
          "description": "Move the source file to an archive path after processing",
          "type": [
            "boolean",
            "null"
          ]
        },
        "archivePath": {
          "title": "Archive path",
          "description": "Archive path pattern, e.g. 'archive/products/{yyyy}/{MM}/{dd}'",
          "type": [
            "string",
            "null"
          ],
          "maxLength": 500
        }
      }
    }
  },
  "required": [
    "name",
    "integrationConnectionId",
    "type",
    "inboundTransport",
    "commandMode",
    "errorStrategy",
    "maxRetries",
    "isActive",
    "mappingSourceType",
    "acceptTestFlag",
    "callTimeoutSeconds",
    "retryBackoffSeconds",
    "deadlineSeconds",
    "acknowledgementExpected"
  ]
} as unknown as ExtendedJsonSchema;

/**
 * UI Schema for IntegrationJob (UPDATE)
 */
export const IntegrationJobUpdateUiSchema: UISchemaElement = {
  "type": "GridLayout",
  "options": {
    "columns": 12,
    "density": "compact",
    "variant": "standard"
  },
  "elements": [
    {
      "type": "Group",
      "label": "Job",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/name",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/isActive",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/type",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/inboundTransport",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/integrationConnectionId",
          "options": {
            "gridColumn": "span 6",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "IntegrationConnection",
              "module": "integration",
              "displayField": "name",
              "searchFields": [
                "name"
              ],
              "additionalFields": [
                "provider"
              ],
              "minSearchLength": 1
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/commandMode",
          "options": {
            "gridColumn": "span 6"
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/defaultOrgUnitId",
          "options": {
            "gridColumn": "span 6",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "OrgUnit",
              "module": "core",
              "displayField": "name",
              "searchFields": [
                "name",
                "code"
              ],
              "additionalFields": [],
              "minSearchLength": 0,
              "presentation": "tree"
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Job",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Mapping Source",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/mappingSourceType",
          "options": {
            "gridColumn": "span 12",
            "allowedValuesWhen": [
              {
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                "values": [
                  "CODED_ADAPTER"
                ]
              },
              {
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                },
                "values": [
                  "CODED_ADAPTER"
                ]
              }
            ]
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/importProfileId",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "entity": "ImportProfile",
              "module": "integration",
              "displayField": "name",
              "searchFields": [
                "name",
                "targetEntity"
              ],
              "additionalFields": [
                "targetEntity",
                "format"
              ],
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "IMPORT_PROFILE"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/adapterName",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "apiMode": true,
              "endpoint": "/api/integration/integrationjob/coded-adapters",
              "valueField": "adapterName",
              "displayField": "displayName",
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "CODED_ADAPTER"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/targetEntityName",
          "options": {
            "gridColumn": "span 12",
            "customRenderer": "LookupRenderer",
            "lookup": {
              "apiMode": true,
              "endpoint": "/api/integration/integrationjob/target-entities",
              "valueField": "entityName",
              "displayField": "displayName",
              "minSearchLength": 0
            }
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "enum": [
                  "DIRECT_DTO",
                  "XSLT_TRANSFORM"
                ]
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.MappingSource",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Error Handling",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/errorStrategy",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxRetries",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "enum": [
                  "EXPORT",
                  "CALL"
                ]
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.ErrorHandling",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Source Files",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/deleteAfterProcess",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFileConfig/properties/archivePath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                },
                {
                  "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
                  "schema": {
                    "const": true
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.SourceFiles",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "File Source",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/sourcePath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceFilePattern",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/sourceEncoding",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "FETCH"
                  }
                },
                {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "enum": [
                      "IMPORT_PROFILE",
                      "DIRECT_DTO"
                    ]
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.FileSource",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Processing",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/processingMode",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deliveryMode",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deliveryPath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Processing",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Channel",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/inboundMessageType",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/messageIdPath",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/verificationProfile",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                },
                {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "XSLT_TRANSFORM"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Channel",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Options",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/acceptTestFlag",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/inlineThresholdBytes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxBodyBytes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                },
                {
                  "scope": "#/properties/inboundTransport",
                  "schema": {
                    "const": "PUSH"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Options",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Inbox search keys",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/key1Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/key2Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/key3Path",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "IMPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Inboxsearchkeys",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Transform",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/transformXslt",
          "options": {
            "gridColumn": "span 12",
            "contentFormat": "xml",
            "multi": true,
            "rows": 5
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/mappingSourceType",
              "schema": {
                "const": "XSLT_TRANSFORM"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Transform",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Trigger",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/triggerEventType",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Trigger",
      "options": {
        "gridColumn": "span 2"
      }
    },
    {
      "type": "Group",
      "label": "Answers",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/acknowledgementExpected",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "EXPORT"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/receiptDeadlineMinutes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                {
                  "scope": "#/properties/acknowledgementExpected",
                  "schema": {
                    "enum": [
                      "RECEIPT",
                      "RECEIPT_AND_ACCEPTANCE"
                    ]
                  }
                }
              ]
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/acceptanceDeadlineMinutes",
          "options": {
            "gridColumn": "span 6"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "type": "AND",
              "conditions": [
                {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                },
                {
                  "scope": "#/properties/acknowledgementExpected",
                  "schema": {
                    "const": "RECEIPT_AND_ACCEPTANCE"
                  }
                }
              ]
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Answers",
      "options": {
        "gridColumn": "span 4"
      }
    },
    {
      "type": "Group",
      "label": "Call",
      "elements": [
        {
          "type": "Control",
          "scope": "#/properties/apiEndpoint",
          "options": {
            "gridColumn": "span 12"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/callTimeoutSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/retryBackoffSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/deadlineSeconds",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        },
        {
          "type": "Control",
          "scope": "#/properties/maxConcurrentCalls",
          "options": {
            "gridColumn": "span 3"
          },
          "rule": {
            "effect": RuleEffect.SHOW,
            "condition": {
              "scope": "#/properties/type",
              "schema": {
                "const": "CALL"
              }
            }
          }
        }
      ],
      "i18n": "Section:IntegrationJob.Call",
      "options": {
        "gridColumn": "span 4"
      }
    }
  ]
};


export interface PanelDescriptor {
  title: string;
  titleKey?: string;
  collapsible?: boolean;
  /** When 'tabs', groups within this panel render as MUI tabs instead of stacked */
  groupLayout?: 'stacked' | 'tabs';
  /** Per-group UISchemas when groupLayout is 'tabs' */
  groupUiSchemas?: Array<{ title: string; titleKey: string; uiSchema: UISchemaElement }>;
  uiSchema: UISchemaElement;
}

/**
 * Panel descriptors for IntegrationJob (CREATE)
 */
export const IntegrationJobCreatePanels: PanelDescriptor[] = [
  {
    "title": "General",
    "titleKey": "Section:IntegrationJob.General",
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Job",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/name",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/isActive",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/type",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/inboundTransport",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/integrationConnectionId",
              "options": {
                "gridColumn": "span 6",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "IntegrationConnection",
                  "module": "integration",
                  "displayField": "name",
                  "searchFields": [
                    "name"
                  ],
                  "additionalFields": [
                    "provider"
                  ],
                  "minSearchLength": 1
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/commandMode",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/defaultOrgUnitId",
              "options": {
                "gridColumn": "span 6",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "OrgUnit",
                  "module": "core",
                  "displayField": "name",
                  "searchFields": [
                    "name",
                    "code"
                  ],
                  "additionalFields": [],
                  "minSearchLength": 0,
                  "presentation": "tree"
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Job",
          "options": {
            "gridColumn": "span 4"
          }
        },
        {
          "type": "Group",
          "label": "Mapping Source",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/mappingSourceType",
              "options": {
                "gridColumn": "span 12",
                "allowedValuesWhen": [
                  {
                    "condition": {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    "values": [
                      "CODED_ADAPTER"
                    ]
                  },
                  {
                    "condition": {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "CALL"
                      }
                    },
                    "values": [
                      "CODED_ADAPTER"
                    ]
                  }
                ]
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/importProfileId",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "ImportProfile",
                  "module": "integration",
                  "displayField": "name",
                  "searchFields": [
                    "name",
                    "targetEntity"
                  ],
                  "additionalFields": [
                    "targetEntity",
                    "format"
                  ],
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "IMPORT_PROFILE"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/adapterName",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "apiMode": true,
                  "endpoint": "/api/integration/integrationjob/coded-adapters",
                  "valueField": "adapterName",
                  "displayField": "displayName",
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "CODED_ADAPTER"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/targetEntityName",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "apiMode": true,
                  "endpoint": "/api/integration/integrationjob/target-entities",
                  "valueField": "entityName",
                  "displayField": "displayName",
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "enum": [
                      "DIRECT_DTO",
                      "XSLT_TRANSFORM"
                    ]
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.MappingSource",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Error Handling",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/errorStrategy",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxRetries",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "enum": [
                      "EXPORT",
                      "CALL"
                    ]
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.ErrorHandling",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Source Files",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/deleteAfterProcess",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/archivePath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    },
                    {
                      "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
                      "schema": {
                        "const": true
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.SourceFiles",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "Source & Processing",
    "titleKey": "Section:IntegrationJob.Source&Processing",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "File Source",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/sourcePath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFilePattern",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceEncoding",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    },
                    {
                      "scope": "#/properties/mappingSourceType",
                      "schema": {
                        "enum": [
                          "IMPORT_PROFILE",
                          "DIRECT_DTO"
                        ]
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.FileSource",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Processing",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/processingMode",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deliveryMode",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deliveryPath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Processing",
          "options": {
            "gridColumn": "span 2"
          }
        }
      ]
    }
  },
  {
    "title": "Inbound",
    "titleKey": "Section:IntegrationJob.Inbound",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Channel",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/inboundMessageType",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/messageIdPath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/verificationProfile",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    },
                    {
                      "scope": "#/properties/mappingSourceType",
                      "schema": {
                        "const": "XSLT_TRANSFORM"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Channel",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Options",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/acceptTestFlag",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/inlineThresholdBytes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxBodyBytes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Options",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Inbox search keys",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/key1Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/key2Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/key3Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Inboxsearchkeys",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Transform",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/transformXslt",
              "options": {
                "gridColumn": "span 12",
                "contentFormat": "xml",
                "multi": true,
                "rows": 5
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "XSLT_TRANSFORM"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Transform",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "Outbound Export",
    "titleKey": "Section:IntegrationJob.OutboundExport",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Trigger",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/triggerEventType",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Trigger",
          "options": {
            "gridColumn": "span 2"
          }
        }
      ]
    }
  },
  {
    "title": "Acknowledgement",
    "titleKey": "Section:IntegrationJob.Acknowledgement",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Answers",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/acknowledgementExpected",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/receiptDeadlineMinutes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    {
                      "scope": "#/properties/acknowledgementExpected",
                      "schema": {
                        "enum": [
                          "RECEIPT",
                          "RECEIPT_AND_ACCEPTANCE"
                        ]
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/acceptanceDeadlineMinutes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    {
                      "scope": "#/properties/acknowledgementExpected",
                      "schema": {
                        "const": "RECEIPT_AND_ACCEPTANCE"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Answers",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "External Call",
    "titleKey": "Section:IntegrationJob.ExternalCall",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Call",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/apiEndpoint",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/callTimeoutSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/retryBackoffSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deadlineSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxConcurrentCalls",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Call",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  }
];

/**
 * Panel descriptors for IntegrationJob (UPDATE)
 */
export const IntegrationJobUpdatePanels: PanelDescriptor[] = [
  {
    "title": "General",
    "titleKey": "Section:IntegrationJob.General",
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Job",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/name",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/isActive",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/type",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/inboundTransport",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/integrationConnectionId",
              "options": {
                "gridColumn": "span 6",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "IntegrationConnection",
                  "module": "integration",
                  "displayField": "name",
                  "searchFields": [
                    "name"
                  ],
                  "additionalFields": [
                    "provider"
                  ],
                  "minSearchLength": 1
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/commandMode",
              "options": {
                "gridColumn": "span 6"
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/defaultOrgUnitId",
              "options": {
                "gridColumn": "span 6",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "OrgUnit",
                  "module": "core",
                  "displayField": "name",
                  "searchFields": [
                    "name",
                    "code"
                  ],
                  "additionalFields": [],
                  "minSearchLength": 0,
                  "presentation": "tree"
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Job",
          "options": {
            "gridColumn": "span 4"
          }
        },
        {
          "type": "Group",
          "label": "Mapping Source",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/mappingSourceType",
              "options": {
                "gridColumn": "span 12",
                "allowedValuesWhen": [
                  {
                    "condition": {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    "values": [
                      "CODED_ADAPTER"
                    ]
                  },
                  {
                    "condition": {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "CALL"
                      }
                    },
                    "values": [
                      "CODED_ADAPTER"
                    ]
                  }
                ]
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/importProfileId",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "entity": "ImportProfile",
                  "module": "integration",
                  "displayField": "name",
                  "searchFields": [
                    "name",
                    "targetEntity"
                  ],
                  "additionalFields": [
                    "targetEntity",
                    "format"
                  ],
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "IMPORT_PROFILE"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/adapterName",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "apiMode": true,
                  "endpoint": "/api/integration/integrationjob/coded-adapters",
                  "valueField": "adapterName",
                  "displayField": "displayName",
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "CODED_ADAPTER"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/targetEntityName",
              "options": {
                "gridColumn": "span 12",
                "customRenderer": "LookupRenderer",
                "lookup": {
                  "apiMode": true,
                  "endpoint": "/api/integration/integrationjob/target-entities",
                  "valueField": "entityName",
                  "displayField": "displayName",
                  "minSearchLength": 0
                }
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "enum": [
                      "DIRECT_DTO",
                      "XSLT_TRANSFORM"
                    ]
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.MappingSource",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Error Handling",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/errorStrategy",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxRetries",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "enum": [
                      "EXPORT",
                      "CALL"
                    ]
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.ErrorHandling",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Source Files",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/deleteAfterProcess",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFileConfig/properties/archivePath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    },
                    {
                      "scope": "#/properties/sourceFileConfig/properties/archiveEnabled",
                      "schema": {
                        "const": true
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.SourceFiles",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "Source & Processing",
    "titleKey": "Section:IntegrationJob.Source&Processing",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "File Source",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/sourcePath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceFilePattern",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/sourceEncoding",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "FETCH"
                      }
                    },
                    {
                      "scope": "#/properties/mappingSourceType",
                      "schema": {
                        "enum": [
                          "IMPORT_PROFILE",
                          "DIRECT_DTO"
                        ]
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.FileSource",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Processing",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/processingMode",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deliveryMode",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deliveryPath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Processing",
          "options": {
            "gridColumn": "span 2"
          }
        }
      ]
    }
  },
  {
    "title": "Inbound",
    "titleKey": "Section:IntegrationJob.Inbound",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Channel",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/inboundMessageType",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/messageIdPath",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/verificationProfile",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    },
                    {
                      "scope": "#/properties/mappingSourceType",
                      "schema": {
                        "const": "XSLT_TRANSFORM"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Channel",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Options",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/acceptTestFlag",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/inlineThresholdBytes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxBodyBytes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "IMPORT"
                      }
                    },
                    {
                      "scope": "#/properties/inboundTransport",
                      "schema": {
                        "const": "PUSH"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Options",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Inbox search keys",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/key1Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/key2Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/key3Path",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "IMPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Inboxsearchkeys",
          "options": {
            "gridColumn": "span 2"
          }
        },
        {
          "type": "Group",
          "label": "Transform",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/transformXslt",
              "options": {
                "gridColumn": "span 12",
                "contentFormat": "xml",
                "multi": true,
                "rows": 5
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/mappingSourceType",
                  "schema": {
                    "const": "XSLT_TRANSFORM"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Transform",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "Outbound Export",
    "titleKey": "Section:IntegrationJob.OutboundExport",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Trigger",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/triggerEventType",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Trigger",
          "options": {
            "gridColumn": "span 2"
          }
        }
      ]
    }
  },
  {
    "title": "Acknowledgement",
    "titleKey": "Section:IntegrationJob.Acknowledgement",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Answers",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/acknowledgementExpected",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "EXPORT"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/receiptDeadlineMinutes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    {
                      "scope": "#/properties/acknowledgementExpected",
                      "schema": {
                        "enum": [
                          "RECEIPT",
                          "RECEIPT_AND_ACCEPTANCE"
                        ]
                      }
                    }
                  ]
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/acceptanceDeadlineMinutes",
              "options": {
                "gridColumn": "span 6"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "type": "AND",
                  "conditions": [
                    {
                      "scope": "#/properties/type",
                      "schema": {
                        "const": "EXPORT"
                      }
                    },
                    {
                      "scope": "#/properties/acknowledgementExpected",
                      "schema": {
                        "const": "RECEIPT_AND_ACCEPTANCE"
                      }
                    }
                  ]
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Answers",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  },
  {
    "title": "External Call",
    "titleKey": "Section:IntegrationJob.ExternalCall",
    "collapsible": true,
    "uiSchema": {
      "type": "GridLayout",
      "options": {
        "columns": 12,
        "density": "compact",
        "variant": "standard"
      },
      "elements": [
        {
          "type": "Group",
          "label": "Call",
          "elements": [
            {
              "type": "Control",
              "scope": "#/properties/apiEndpoint",
              "options": {
                "gridColumn": "span 12"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/callTimeoutSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/retryBackoffSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/deadlineSeconds",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            },
            {
              "type": "Control",
              "scope": "#/properties/maxConcurrentCalls",
              "options": {
                "gridColumn": "span 3"
              },
              "rule": {
                "effect": RuleEffect.SHOW,
                "condition": {
                  "scope": "#/properties/type",
                  "schema": {
                    "const": "CALL"
                  }
                }
              }
            }
          ],
          "i18n": "Section:IntegrationJob.Call",
          "options": {
            "gridColumn": "span 4"
          }
        }
      ]
    }
  }
];


export interface PageTabContentItem {
  type: 'panel' | 'childGrid' | 'extension';
  panelIndex?: number;
  childGrid?: string;
  extension?: string;
}

export interface PageTabDescriptor {
  name: string;
  nameKey: string;
  default: boolean;
  panels: PanelDescriptor[];
  content: PageTabContentItem[];
}

export const IntegrationJobCreatePageTabs: PageTabDescriptor[] | null = null;
export const IntegrationJobUpdatePageTabs: PageTabDescriptor[] | null = null;

/**
 * Default values for new IntegrationJob (extracted from schema defaults)
 */
export const IntegrationJobCreateDefaults: Partial<Record<string, any>> = {
  "inboundTransport": "FETCH",
  "errorStrategy": "QUARANTINE",
  "maxRetries": 3,
  "acceptTestFlag": false,
  "inlineThresholdBytes": 1048576,
  "maxBodyBytes": 20971520,
  "sourceEncoding": "utf-8",
  "callTimeoutSeconds": 30,
  "retryBackoffSeconds": 10,
  "deadlineSeconds": 600,
  "acknowledgementExpected": "NONE"
};

/** Compute create defaults (includes dynamic date defaults resolved at call time). */
export function getIntegrationJobCreateDefaults(): Partial<Record<string, any>> {
  return { ...IntegrationJobCreateDefaults };
}

// Legacy exports (for backward compatibility - use create mode)
export const IntegrationJobSchema = IntegrationJobCreateSchema;
export const IntegrationJobUiSchema = IntegrationJobCreateUiSchema;
